import React, { useEffect, useState } from "react";
import Text from "@amzn/meridian/text";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Input from "@amzn/meridian/input";
import { useAppSelector } from "src/store/store";
import { useDispatch } from "react-redux";
import { BackgroundFormData, getEmployeeInfo, JmrFlowTypes, setBackgroundFormData } from "src/store/newJmrPageSlice";
import Box from "@amzn/meridian/box";
import Select, { SelectOption } from "@amzn/meridian/select";
import searchTokens from "@amzn/meridian-tokens/base/icon/search";
import { createSearchRegExp } from "src/utils/searchUtils";
import Icon from "@amzn/meridian/icon";
import { PAPI_FIELDS } from "src/config/PapiFields";
import CaseSource from "./caseSource/caseSource";
import {useParams} from "react-router-dom";
import {AUSTIN} from "src/components/pages/newJMR/backgroundPage/associateInformation/caseSource/constant";
import {getExampleCaseIdText} from "src/utils/helpers";

interface associateInformationProps {
  handleChange: (key: keyof BackgroundFormData, value: any) => void;
}

const associateInformation = ({ handleChange }: associateInformationProps) => {
  const { backgroundFormData, backgroundFormErrors, jmrFlowType } = useAppSelector((state) => state.newJmrPage);
  const { caseSource } = useAppSelector((state) => state.newJmrPage.backgroundFormData);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const dispatch = useDispatch();
  const { caseId, login, type } = useParams();
  const [siteSearchQuery, setSiteSearchQuery] = useState();
  const siteSearchRegExp = createSearchRegExp(siteSearchQuery);
  const matchedSiteOptions = allowedSites
    ?.map((site) => ({ label: site.site, value: site.site }))
    .filter((option) => !siteSearchQuery || siteSearchRegExp.test(option.label));

  useEffect(() => {
    const siteInAllowedSites = allowedSites?.filter((s) => s.site === backgroundFormData.siteLocation)[0];

    if (backgroundFormData.siteLocation && backgroundFormData.siteLocation !== backgroundFormData.site?.site) {
      dispatch(
        setBackgroundFormData({
          ...backgroundFormData,
          site: siteInAllowedSites,
          siteLocation: siteInAllowedSites ? backgroundFormData.siteLocation : undefined,
        })
      );
    }
  }, [backgroundFormData.siteLocation]);

  // prefill data when access /newJMR/:type/:login/:caseId
  useEffect(() => {
    if (login && caseId && type) {
      dispatch(getEmployeeInfo(login));
      dispatch(setBackgroundFormData({
          ...backgroundFormData,
          alias: login,
          caseID: caseId,
          caseSource: AUSTIN,
      }));
    }
  }, []);

  // Create input field based on field name
  const createInputField = (
    fieldName: keyof BackgroundFormData,
    label: string,
    placeholder: string,
    disabled: boolean = true,
    className?: string,
    children?: any,
    handleChangeWrapper = handleChange
  ) => {
    return (
      <Column spacing="200" className={className}>
        <Text tag="label" type="b200" color="primary">
          {label}
        </Text>
        <Input
          data-cy={`new-jmr__background__${fieldName}`}
          type="text"
          placeholder={placeholder}
          value={backgroundFormData[fieldName] as string}
          onChange={(value) => {
            handleChangeWrapper(fieldName, value);
          }}
          error={backgroundFormErrors[fieldName]?.error}
          errorMessage={backgroundFormErrors[fieldName]?.errorMessage}
          disabled={disabled && (jmrFlowType !== JmrFlowTypes.CreateNewAccommodation || PAPI_FIELDS.includes(fieldName))}
        >
          {children}
        </Input>
      </Column>
    );
  };

  return (
    <Box>
      <Column spacing={"200"}>
        <Text type="h300">Associate Information</Text>
        <Text type="b300" color="secondary">
          Enter and search for the Associate’s alias. Fill out any missing information.
        </Text>
      </Column>
      <Row widths={"fill"} spacingInset={"400 none 300 none"} alignmentVertical={"top"}>
        {createInputField(
          "alias",
          "Alias",
          "Associate's alias",
          true,
          "new-jmr__background-page__alias",
          <div
            data-cy="new-jmr__background__alias-search"
            className="new-jmr__background-page__alias__search-icon"
            onClick={() => dispatch(getEmployeeInfo(backgroundFormData.alias))}
          >
            <Icon tokens={searchTokens} />
          </div>,
          (key, value) => handleChange(key, value.replace(/[^A-Za-z0-9]/g, ""))
        )}

        {createInputField("fullName", "Full Name", "Associate’s first and last name")}
      </Row>
      <Row widths={"fill"} spacingInset={"300 none"} alignmentVertical={"top"}>
        <Column spacing="200">
          <Text tag="label" type="b200" color="primary">
            Site Location
          </Text>
          <Select
            data-cy="new-jmr__background__siteLocation"
            searchQuery={siteSearchQuery}
            onSearch={setSiteSearchQuery}
            placeholder="Associate’s job site"
            value={backgroundFormData.siteLocation}
            onChange={(siteLocation) => {
              handleChange("siteLocation", siteLocation);
            }}
            error={backgroundFormErrors.siteLocation?.error}
            errorMessage={backgroundFormErrors.siteLocation?.errorMessage}
            disabled={jmrFlowType !== JmrFlowTypes.CreateNewAccommodation}
          >
            {matchedSiteOptions.map((option) => (
              <SelectOption
                data-cy="new-jmr__background__siteLocation-item"
                label={option.label}
                value={option.value}
                key={option.value}
              />
            ))}
            {!matchedSiteOptions.length ? (
              <Column alignmentVertical="center" spacing="300" spacingInset="500">
                <Text alignment="center">No sites found</Text>
              </Column>
            ) : null}
          </Select>
        </Column>
        {createInputField("job", "Job", "Associate’s assigned job")}
      </Row>
      <Row widths={"fill"} spacingInset={"300 none"} alignmentVertical={"top"}>
        {createInputField("employeeID", "Employee ID", "Associate’s employee ID")}
        {createInputField("shift", "Shift", "Shift Code (ie. DCA-0730)", false)}
      </Row>
      <Row widths={"fill"} spacingInset={"300 none"} alignmentVertical={"top"}>
        {createInputField("shiftLength", "Normal Shift Length (hours)", "Shift length by hours", false)}
        {createInputField("managerName", "Manager's Full Name", "Manager's first and last name")}
      </Row>
      <Row widths={"fill"} spacingInset={"300 none"} alignmentVertical={"top"}>
        {createInputField("managerAlias", "Manager Alias", "Manager's alias")}
        {createInputField("caseID", "Case ID", `${getExampleCaseIdText(caseSource)}`)}
      </Row>
      <CaseSource
        handleChange={handleChange}
        isNewJMR={jmrFlowType === JmrFlowTypes.CreateNewAccommodation}
      ></CaseSource>
    </Box>
  );
};

export default associateInformation;
